import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import { Search, ShieldCheck, AlertTriangle, CheckCircle, Radio } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';

export default function RideVerifications() {
  const [search, setSearch] = useState('');
  const [suspiciousFilter, setSuspiciousFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['fleet-ride-verifications', suspiciousFilter, startDate, endDate, search, page],
    queryFn: () =>
      fleetService.getRideVerifications({
        page,
        limit: 15,
        isSuspicious: suspiciousFilter,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        rideId: search || undefined,
      }).then((res) => res.data),
  });

  const verifications = data?.data || [];
  const pagination = data?.pagination || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-text dark:text-text-dark mb-2 flex items-center gap-3">
            <ShieldCheck className="w-7 h-7 text-primary" />
            Ride Verifications
          </h1>
          <p className="text-text-secondary dark:text-text-secondary-dark">
            Monitor GPS verification data for your fleet rides
          </p>
        </div>
      </div>

      <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark shadow-lg overflow-hidden">
        {/* Filters */}
        <div className="p-4 lg:p-6 border-b border-border dark:border-border-dark bg-gradient-to-r from-surface-secondary/50 to-transparent dark:from-surface-secondary-dark/50">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-tertiary dark:text-text-tertiary-dark" />
              <input
                type="text"
                placeholder="Search by Ride ID..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-10 pr-4 py-3 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              />
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                className="px-3 py-2 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-sm text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="text-text-secondary dark:text-text-secondary-dark text-sm">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                className="px-3 py-2 bg-surface-secondary dark:bg-surface-secondary-dark border border-border dark:border-border-dark rounded-xl text-sm text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {['', 'true', 'false'].map((val) => (
                <button
                  key={val}
                  onClick={() => { setSuspiciousFilter(val); setPage(1); }}
                  className={cn(
                    'px-4 py-2 rounded-xl font-medium transition-all text-sm',
                    suspiciousFilter === val
                      ? val === 'true' ? 'bg-danger text-white shadow-lg' : val === 'false' ? 'bg-success text-white shadow-lg' : 'bg-primary text-white shadow-lg'
                      : 'bg-surface-secondary dark:bg-surface-secondary-dark text-text-secondary dark:text-text-secondary-dark hover:scale-105'
                  )}
                >
                  {val === '' ? 'All' : val === 'true' ? 'Suspicious' : 'Clean'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
            <div className="text-text-secondary dark:text-text-secondary-dark">Loading verifications...</div>
          </div>
        ) : verifications.length === 0 ? (
          <div className="p-12 text-center text-text-secondary dark:text-text-secondary-dark">
            No ride verifications found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-secondary dark:bg-surface-secondary-dark">
                <tr>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">Ride ID</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">Driver</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden md:table-cell">Rider</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden lg:table-cell">Start Time</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden lg:table-cell">End Time</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">Status</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden sm:table-cell">Suspicious</th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider hidden xl:table-cell">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border dark:divide-border-dark">
                {verifications.map((v) => (
                  <tr
                    key={v.id}
                    className="hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark cursor-pointer"
                    onClick={() => navigate(`/ride-verifications/${v.id}`)}
                  >
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-mono text-primary">
                      {v.rideId?.slice(0, 8)}...
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text dark:text-text-dark">
                      {v.driver?.name || 'N/A'}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text dark:text-text-dark hidden md:table-cell">
                      {v.customer?.name || 'N/A'}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text-secondary dark:text-text-secondary-dark hidden lg:table-cell">
                      {formatDate(v.startTimestamp)}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text-secondary dark:text-text-secondary-dark hidden lg:table-cell">
                      {formatDate(v.endTimestamp)}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <span className={cn(
                        'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                        v.status === 'LIVE'
                          ? 'bg-info/10 text-info'
                          : 'bg-success-light dark:bg-success-dark text-success'
                      )}>
                        {v.status === 'LIVE' ? <Radio className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                        {v.status}
                      </span>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                      {v.isSuspicious ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-danger-light dark:bg-danger-dark text-danger">
                          <AlertTriangle className="w-3 h-3" />
                          Yes
                        </span>
                      ) : (
                        <span className="text-xs text-text-tertiary dark:text-text-tertiary-dark">No</span>
                      )}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-text-secondary dark:text-text-secondary-dark hidden xl:table-cell">
                      {formatDate(v.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="p-4 lg:p-6 border-t border-border dark:border-border-dark flex flex-col sm:flex-row items-center justify-between gap-4 bg-surface-secondary/30 dark:bg-surface-secondary-dark/30">
            <div className="text-sm text-text-secondary dark:text-text-secondary-dark">
              Showing <span className="font-semibold text-text dark:text-text-dark">{verifications.length}</span> of{' '}
              <span className="font-semibold text-text dark:text-text-dark">{pagination.total || 0}</span> records
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors text-sm font-medium"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!pagination.pages || page >= pagination.pages}
                className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-dark transition-colors text-sm font-medium shadow-lg"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
